import type { MouseEvent } from 'react';
import type { ShotMark } from '@studio/shared';

import { useT } from '../lib/i18n';
import { CheckIcon, RejectIcon } from './Icons';

/**
 * Keep it, or set it aside — said in words.
 *
 * This has been a grease-pencil ring and cross drawn over the photograph, then
 * a pair of boxes holding a tick and a cross. Both asked the reader to learn a
 * code: which mark means which, and what an unmarked frame means. Two words do
 * not need learning, and each carries its sign — a tick, a cross — so the
 * pair reads at a glance. The chosen one fills — ink to keep, grey to set
 * aside — and nothing is ever drawn on the picture, which is the one thing on
 * the card that is being judged.
 *
 * Pressing the answer a frame already has clears it, so a mis-click costs
 * nothing and "not decided yet" stays reachable. Rendered inside or over the
 * frame's own button, so every handler stops propagation: deciding about a
 * frame must not also open it.
 */
export function MarkStrip({
  mark,
  onMark,
  size = 'md',
}: {
  mark: ShotMark | undefined;
  onMark: (next: ShotMark | null) => void;
  /** `sm` on a card or a contact sheet; the default where there is room. */
  size?: 'sm' | 'md' | 'named';
}) {
  const t = useT();
  const press = (value: ShotMark) => (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onMark(mark === value ? null : value);
  };

  return (
    <span
      className={`verdict ${size === 'sm' ? 'verdict--sm' : ''}`}
      role="group"
      aria-label={t('loupe.verdict')}
    >
      <button
        type="button"
        className={`verdict__opt ${mark === 'keep' ? 'is-keep' : ''}`}
        aria-pressed={mark === 'keep'}
        aria-label={t(mark === 'keep' ? 'sheet.markKeepOff' : 'sheet.markKeep')}
        onClick={press('keep')}
      >
        <CheckIcon />
        {t('mark.keep')}
      </button>
      <button
        type="button"
        className={`verdict__opt ${mark === 'reject' ? 'is-reject' : ''}`}
        aria-pressed={mark === 'reject'}
        aria-label={t(mark === 'reject' ? 'sheet.markRejectOff' : 'sheet.markReject')}
        onClick={press('reject')}
      >
        <RejectIcon />
        {t('mark.reject')}
      </button>
    </span>
  );
}
